/* eslint-disable react/require-default-props */
import React from 'react';
import classNames from 'classnames';
import './StatusStyles.scss';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
/**
 * Matches a Quimby color token name such as `green-500`, `gray-700`, or
 * `brand-01` (lowercase word + one or more `-segment` groups).
 */
const TOKEN_PATTERN = /^[a-z]+(?:-[a-z0-9]+)+$/i;
/**
 * Resolve the `color` prop to a CSS color value.
 * - `--gray-500` → `var(--gray-500)` (explicit CSS variable)
 * - `green-500`  → `var(--green-500)` (design-system token name)
 * - anything else (`#CAA100`, `rgb(...)`, `rebeccapurple`) → used as-is
 */
function resolveDotColor(color) {
    if (color.startsWith('--'))
        return `var(${color})`;
    if (TOKEN_PATTERN.test(color))
        return `var(--${color})`;
    return color;
}
/**
 * Status — a compact, read-only indicator that communicates the current state
 * of an item (inspection, deficiency, work order, invoice, etc.). It pairs a
 * `fa-circle` dot with a short text label.
 *
 * Color: set the dot color via the `color` prop using any Quimby design-system
 * color token. This affects the icon only; the label is always black.
 *
 * Read-only: Status does not trigger navigation or actions. The only optional
 * interaction is a hover/focus tooltip.
 *
 * Based on: https://www.figma.com/design/ZCRzifGUbiPZwWVO6jRD58/Quimby?node-id=5726-683
 *
 * @example
 * ```tsx
 * <Status label="Passed" color="green-500" />              // design-system token
 * <Status label="Custom" color="#CAA100" />                // raw color value
 * <Status label="Overdue" color="red-500" tooltip="Inspection is past its due date" />
 * ```
 */
export function Status({ label, color = 'gray-500', tooltip, className }) {
    const status = (React.createElement("span", { className: classNames('qmb-ui-status', className) },
        React.createElement("i", { className: classNames('qmb-ui-status__icon', 'fa-solid', 'fa-circle'), style: { color: resolveDotColor(color) }, "aria-hidden": "true" }),
        React.createElement(Text, { variant: "label", className: "qmb-ui-status__label" }, label)));
    if (tooltip) {
        return React.createElement(Tooltip, { content: tooltip }, status);
    }
    return status;
}
